import { valueProvider, lazyValue, noop, superClassOf, hasOwnProperty } from '@proc7ts/primitives';

/**
 * Converts arbitrary amendment to {@link Amender amender}.
 *
 * @typeParam TAmended - Amended entity type.
 * @param amendment - An amendment to convert.
 *
 * @returns Either the `amendment` itself if it is already an amender, or its {@link Amendatory.applyAmendment
 * applyAmendment} method if it is an {@link Amendatory} instance.
 */
function amenderOf(amendment) {
    return isAmendatoryAmendment(amendment) ? amendment.applyAmendment : amendment;
}
/**
 * Checks if the given amendment is represented by {@link Amendatory} instance.
 *
 * @typeParam TAmended - Amended entity type.
 * @param amendment - An amendment to check.
 *
 * @returns `true` if the given `amendment` has an {@link Amendatory.applyAmendment applyAmendment} method,
 * or `false` otherwise.
 */
function isAmendatoryAmendment(amendment) {
    return typeof amendment.applyAmendment === 'function';
}
/**
 * Checks whether the given value is an {@link Amendatory} instance.
 *
 * @typeParam TAmended - Amended entity type.
 * @typeParam TOther - Another type the `value` may have.
 * @param value - The value to check.
 *
 * @returns `true` if the given `value` is an object or function with {@link Amendatory.applyAmendment applyAmendment}
 * method, or `false` otherwise.
 */
function isAmendatory(value) {
    return !!value
        && (typeof value === 'object' || typeof value === 'function')
        && isAmendatoryAmendment(value);
}

/**
 * An {@link Amender amender} that amends nothing.
 *
 * @param _target - An unused amendment target that remains intact.
 *
 * @returns Itself for convenience.
 */
function noopAmender(_target) {
    return noopAmender;
}

/**
 * Combines multiple amendments into one amender.
 *
 * The resulting amender performs amendments in order. The subsequent amendments receive the amendment targets modified
 * by preceding ones.
 *
 * @typeParam TAmended - Amended entity type.
 * @param amendments - An iterable of amendments to apply in their application order.
 *
 * @returns A combining amender.
 */
function allAmender(amendments) {
    if (Array.isArray(amendments) && amendments.length < 2) {
        const [amender = noopAmender] = amendments;
        return amenderOf(amender);
    }
    return target => {
        let amendBy = (amendment) => {
            const amend = (request) => {
                const result = target.amend(request);
                amendBy = (next) => {
                    const nextTarget = result();
                    amenderOf(next)({
                        ...nextTarget,
                        amend,
                    });
                    return nextTarget;
                };
                return () => ({
                    ...result(),
                    amend,
                });
            };
            amenderOf(amendment)({
                ...target,
                amend,
            });
        };
        for (const amendment of amendments) {
            amendBy(amendment);
        }
    };
}

/**
 * Creates custom amendment target.
 *
 * @typeParam TAmended - Amended entity type.
 * @param options - Options for created amendment target.
 *
 * @returns New amendment target instance.
 */
function newAmendTarget(options) {
    const { base, amend: baseAmend } = options;
    const nextTarget = (createBase) => {
        const nextBase = createBase();
        return {
            ...nextBase,
            amend(nextRequest) {
                const modify = lazyValue(baseAmend(nextBase, nextRequest)
                    || (() => AmendTarget$default$modify(nextBase, nextRequest)));
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                return () => nextTarget(modify);
            },
        };
    };
    return nextTarget(valueProvider(base));
}
function AmendTarget$default$modify(base, request) {
    return { ...base, ...request };
}

function AeClass$target(base) {
    return newAmendTarget({
        base,
        amend: AeClass$target$amend(base),
    });
}
function AeClass$target$amend({ amend }) {
    if (!amend) {
        return noop;
    }
    return (_base, request) => amend(request);
}

/**
 * Creates an amendment (and decorator) for a class.
 *
 * @typeParam TClass - A type of amended class.
 * @typeParam TAmended - A type of the entity representing a class to amend.
 * @param amendments - Amendments to apply.
 *
 * @returns - New class amendment instance.
 */
function AeClass(...amendments) {
    const amender = allAmender(amendments);
    const decorateAmended = (base) => amender(AeClass$target(base));
    const decorator = ((target) => {
        decorateAmended({ amendedClass: target });
    });
    decorator.applyAmendment = amender;
    decorator.decorateAmended = decorateAmended;
    return decorator;
}

const autoAmended = ( /*#__PURE__*/new WeakSet());
/**
 * Issues an auto-amendment of the given class.
 *
 * An amendment is done by {@link AmendableClass.autoAmend autoAmend} static method this function calls.
 *
 * A class is auto-amended at most once. The subsequent calls of this function for the same class would do nothing.
 *
 * Auto-amends a super-class prior to amending the given one.
 *
 * @param amendedClass - A class to auto-amend.
 *
 * @returns Amended class instance.
 */
function amend(amendedClass) {
    if (autoAmended.has(amendedClass)) {
        // Already auto-amended.
        return amendedClass;
    }
    autoAmended.add(amendedClass);
    const superClass = superClassOf(amendedClass);
    if (superClass) {
        // Amend super-class first.
        amend(superClass);
    }
    if (!hasOwnProperty(amendedClass, 'autoAmend')) {
        return amendedClass;
    }
    amendedClass.autoAmend(AeClass$target({ amendedClass }));
    return amendedClass;
}

/**
 * Abstract {@link AmendableClass amendable class} instance.
 *
 * Automatically issues an {@link amend amendment} of any subclass upon instance construction.
 */
class Amendable {
    /**
     * Constructs an amendable class instance.
     *
     * Issues a class amendment, unless the class already amended.
     */
    constructor() {
        amend(new.target);
    }
}

/**
 * Unique symbol used as a pseudo-member key by default.
 */
const PseudoMember__symbol = ( /*#__PURE__*/Symbol('PseudoMember'));

function AeProp$notReadable(host, key) {
    return _instance => {
        throw new TypeError(`${host.kind.pName} ${host.cls.name}${AmendProp$accessString(key)} is not readable`);
    };
}
function AeProp$notWritable(host, key) {
    return (_instance, _update) => {
        throw new TypeError(`${host.kind.pName} ${host.cls.name}${AmendProp$accessString(key)} is not writable`);
    };
}
const AeProp$idPattern = /^[a-z_$][a-z0-9_$]*$/i;
function AmendProp$accessString(key) {
    if (typeof key === 'string') {
        return AeProp$idPattern.test(key) ? `.${key}` : `[${JSON.stringify(key)}]`;
    }
    return `[${String(key)}]`;
}

function createAePropAccessor(host, key, descriptor) {
    if (descriptor) {
        const { get, set } = descriptor;
        if (get || set) {
            return [
                get
                    ? instance => get.call(instance)
                    : AeProp$notReadable(host, key),
                set
                    ? (instance, update) => set.call(instance, update)
                    : AeProp$notWritable(host, key),
                noop,
            ];
        }
    }
    const valueKey = Symbol(host.kind.vDesc(key));
    let getValue = (host) => host[key];
    let setValue = (host, update) => {
        host[key] = update;
    };
    const writeValue = (host, update) => {
        host[valueKey] = update;
    };
    let toAccessor;
    if (descriptor && ('value' in descriptor || 'writable' in descriptor)) {
        const { value, writable } = descriptor;
        if (writable) {
            toAccessor = () => {
                getValue = host => valueKey in host
                    ? host[valueKey]
                    : host[valueKey] = value;
                setValue = writeValue;
            };
        }
        else {
            setValue = AeProp$notWritable(host, key);
            toAccessor = noop;
        }
    }
    else {
        toAccessor = () => {
            const superProto = Reflect.getPrototypeOf(host.host);
            if (superProto != null) {
                getValue = hostInstance => {
                    if (valueKey in hostInstance) {
                        return hostInstance[valueKey];
                    }
                    return hostInstance[valueKey] = Reflect.get(superProto, key, hostInstance);
                };
            }
            else {
                getValue = instance => instance[valueKey];
            }
            setValue = writeValue;
        };
    }
    return [
        instance => getValue(instance),
        (instance, update) => setValue(instance, update),
        toAccessor,
    ];
}

function createAePropApplicator(host, amender, key, init) {
    return (baseTarget) => {
        const result = { ...init };
        amender(newAmendTarget({
            base: {
                ...baseTarget,
                key,
                ...init,
            },
            amend(_base, request = {}) {
                const { key: $key, enumerable = result.enumerable, configurable = result.configurable, readable: $readable, writable: $writable, get: $get, set: $set, ...baseRequest } = request;
                const createBaseTarget = baseTarget.amend(baseRequest);
                let { get, set } = request;
                let readable;
                let writable;
                if (!set) {
                    if (get) {
                        set = AeProp$notWritable(host, key);
                        writable = false;
                        readable = true;
                    }
                    else {
                        // Neither get, not set provided.
                        // Accessor remains the same.
                        ({ readable, writable, get, set } = result);
                    }
                }
                else if (get) {
                    readable = true;
                    writable = true;
                }
                else {
                    get = AeProp$notReadable(host, key);
                    readable = false;
                    writable = true;
                }
                result.configurable = configurable;
                result.enumerable = enumerable;
                result.readable = readable;
                result.writable = writable;
                result.get = get;
                result.set = set;
                return () => ({
                    ...createBaseTarget(),
                    key,
                    enumerable,
                    configurable,
                    readable,
                    writable,
                    get,
                    set,
                });
            },
        }));
        return result;
    };
}

function AeProp(createHost, hostClass, amendments) {
    const amender = allAmender(amendments);
    const decorateAmended = (decorated, key, descriptor) => {
        const host = createHost(decorated);
        const [getValue, setValue, toAccessor] = createAePropAccessor(host, key, descriptor);
        const init = {
            enumerable: !descriptor || !!descriptor.enumerable,
            configurable: !descriptor || !!descriptor.configurable,
            readable: !descriptor || !!descriptor.get,
            writable: !descriptor || !!descriptor.set || !!descriptor.writable,
            get: hostInstance => getValue(hostInstance),
            set: (hostInstance, update) => setValue(hostInstance, update),
        };
        const applyAmendment = createAePropApplicator(host, amender, key, init);
        let desc;
        AeClass(classTarget => {
            desc = applyAmendment(classTarget);
        }).decorateAmended(decorated);
        const { enumerable, configurable, get, set } = desc;
        let newDescriptor;
        if (set !== init.set || get !== init.get) {
            newDescriptor = {
                enumerable,
                configurable,
                get() {
                    return get(this);
                },
                set(update) {
                    set(this, update);
                },
            };
            toAccessor();
        }
        else if (enumerable !== init.enumerable || configurable !== init.configurable) {
            if (descriptor && (descriptor.get || descriptor.set)) {
                newDescriptor = {
                    ...descriptor,
                    enumerable,
                    configurable,
                };
            }
            else {
                newDescriptor = {
                    ...descriptor,
                    enumerable,
                    configurable,
                    writable: desc.writable,
                };
            }
        }
        if (newDescriptor && !descriptor) {
            // Decorated field.
            // Declare accessor.
            Reflect.defineProperty(host.host, key, newDescriptor);
        }
        return newDescriptor;
    };
    const decorator = ((targetHost, key, descriptor) => {
        const aeClass = { amendedClass: hostClass(targetHost) };
        return decorateAmended(aeClass, key, descriptor);
    });
    decorator.decorateAmended = decorateAmended;
    decorator.applyAmendment = amender;
    return decorator;
}

function PseudoProp(createHost, { key, get, set }, amendments) {
    const amender = allAmender(amendments);
    const decorateAmended = (decorated, memberKey) => {
        if (key) {
            memberKey = key;
        }
        else if (!memberKey) {
            memberKey = PseudoMember__symbol;
        }
        const host = createHost(decorated);
        const init = {
            enumerable: false,
            configurable: true,
            readable: !!get,
            writable: !!set,
            get: get || AeProp$notReadable(host, memberKey),
            set: set || AeProp$notWritable(host, memberKey),
        };
        const applyAmendment = createAePropApplicator(host, amender, memberKey, init);
        AeClass(applyAmendment).decorateAmended(decorated);
    };
    const decorator = ((amendedClass) => {
        const aeClass = { amendedClass };
        return decorateAmended(aeClass);
    });
    decorator.decorateAmended = decorateAmended;
    decorator.applyAmendment = (target) => {
        decorateAmended(target, target.key);
    };
    return decorator;
}

/**
 * Creates an amendment (and decorator) for the class instance member.
 *
 * @typeParam TValue - Amended member value type.
 * @typeParam TClass - A type of amended class.
 * @typeParam TUpdate - Amended member update type accepted by its setter.
 * @typeParam TAmended - A type of the entity representing a member to amend.
 * @param amendments - Amendments to apply.
 *
 * @returns New class member amendment instance.
 */
function AeMember(...amendments) {
    return AeProp(AeMember$createHost, AeMember$hostClass, amendments);
}
const AeMember$HostKind = {
    pName: 'Property',
    vDesc: key => `valueOf(${String(key)}`,
};
function AeMember$createHost({ amendedClass }) {
    return {
        kind: AeMember$HostKind,
        cls: amendedClass,
        host: amendedClass.prototype,
    };
}
function AeMember$hostClass(proto) {
    return proto.constructor;
}

/**
 * Amends a member (property) of the class.
 *
 * Applies the given amendments to the own property of the target class prototype.
 *
 * @typeParam TClass - A type of amended class.
 * @typeParam TKey - A type of amended member key.
 * @typeParam TAmended - A type of the entity representing a member to amend.
 * @param decorated - Decorated class representation.
 * @param memberKey - A key of the member to amend.
 * @param amendments - Amendment to apply.
 */
function amendMemberOf(decorated, memberKey, ...amendments) {
    const amendment = AeMember(...amendments);
    const proto = decorated.amendedClass.prototype;
    const sourceDesc = Reflect.getOwnPropertyDescriptor(proto, memberKey);
    const amendedDesc = amendment.decorateAmended(decorated, memberKey, sourceDesc);
    if (amendedDesc && sourceDesc) {
        // Redefine the property.
        Reflect.defineProperty(proto, memberKey, amendedDesc);
    }
}

/**
 * Creates a class amendment (and decorator) that amends its members.
 *
 * @typeParam TClass - A type of amended class.
 * @typeParam TExtClass - A type of class extended by the amendment.
 * @typeParam TAmended - A type of the entity representing a class to amend.
 * @param def - A map of member amendments.
 *
 * @returns New class amendment instance.
 */
function AeMembers(def) {
    return AeClass(target => {
        for (const key of Reflect.ownKeys(def)) {
            const amendment = def[key];
            if (amendment) {
                amendMemberOf(target, key, amendment);
            }
        }
    });
}

/**
 * Creates a class amendment (and decorator) that declares a pseudo-member of the target class.
 *
 * A pseudo-member does not alter the class prototype or instance. Instead, its value is read and written by the given
 * `accessor`.
 *
 * @typeParam TValue - Pseudo-member value type.
 * @typeParam TClass - A type of amended class.
 * @typeParam TUpdate - Pseudo-member update type accepted by its setter.
 * @typeParam TAmended - A type of the entity representing a pseudo-member.
 * @param accessor - Pseudo-member accessor.
 * @param amendments - Amendments to apply to pseudo-member.
 *
 * @returns New pseudo-member amendment.
 */
function PseudoMember(accessor, ...amendments) {
    return PseudoProp(PseudoMember$createHost, accessor, amendments);
}
const PseudoMember$HostKind = {
    pName: 'Pseudo-property',
};
function PseudoMember$createHost({ amendedClass }) {
    return {
        kind: PseudoMember$HostKind,
        cls: amendedClass,
        host: amendedClass.prototype,
    };
}

/**
 * Creates an amendment (and decorator) for the static class member.
 *
 * @typeParam TValue - Amended member value type.
 * @typeParam TClass - A type of amended class.
 * @typeParam TUpdate - Amended member update type accepted by its setter.
 * @typeParam TAmended - A type of the entity representing a static member to amend.
 * @param amendments - Amendments to apply.
 *
 * @returns - New static member amendment instance.
 */
function AeStatic(...amendments) {
    return AeProp(AeStatic$createHost, AeStatic$hostClass, amendments);
}
const AeStatic$HostKind = {
    pName: 'Static property',
    vDesc: key => `staticOf(${String(key)}`,
};
function AeStatic$createHost({ amendedClass }) {
    return {
        kind: AeStatic$HostKind,
        cls: amendedClass,
        host: amendedClass,
    };
}
function AeStatic$hostClass(classConstructor) {
    return classConstructor;
}

/**
 * Amends a static member (static property) of the class.
 *
 * Applies the given amendments to the own property of the target class constructor.
 *
 * @typeParam TClass - A type of amended class.
 * @typeParam TKey - A type of amended static member key.
 * @typeParam TAmended - A type of the entity representing a static member to amend.
 * @param decorated - Decorated class representation.
 * @param memberKey - A key of the member to amend.
 * @param amendments - Amendment to apply.
 */
function amendStaticOf(decorated, memberKey, ...amendments) {
    const amendment = AeStatic(...amendments);
    const targetClass = decorated.amendedClass;
    const sourceDesc = Reflect.getOwnPropertyDescriptor(targetClass, memberKey);
    const amendedDesc = amendment.decorateAmended(decorated, memberKey, sourceDesc);
    if (amendedDesc && sourceDesc) {
        // Redefine the property.
        Reflect.defineProperty(targetClass, memberKey, amendedDesc);
    }
}

/**
 * Creates a class amendment (and decorator) that amends its static members.
 *
 * @typeParam TClass - A type of amended class.
 * @typeParam TExtClass - A type of class extended by the amendment.
 * @typeParam TAmended - A type of the entity representing a class to amend.
 * @param def - A map of static member amendments.
 *
 * @returns New class amendment instance.
 */
function AeStatics(def) {
    return AeClass(target => {
        for (const key of Reflect.ownKeys(def)) {
            const amendment = def[key];
            if (amendment) {
                amendStaticOf(target, key, amendment);
            }
        }
    });
}

/**
 * Creates a class amendment (and decorator) that declares a static pseudo-member of the target class.
 *
 * A static pseudo-member does not alter the class constructor. Instead, its value is read and written by the given
 * `accessor`.
 *
 * @typeParam TValue - Static pseudo-member value type.
 * @typeParam TClass - A type of amended class.
 * @typeParam TUpdate - Static pseudo-member update type accepted by its setter.
 * @typeParam TAmended - A type of the entity representing a static pseudo-member.
 * @param accessor - Static pseudo-member accessor.
 * @param amendments - Amendments to apply to static pseudo-member.
 *
 * @returns New static pseudo-member amendment.
 */
function PseudoStatic(accessor, ...amendments) {
    return PseudoProp(PseudoStatic$createHost, accessor, amendments);
}
const PseudoStatic$HostKind = {
    pName: 'Static pseudo-property',
};
function PseudoStatic$createHost({ amendedClass }) {
    return {
        kind: PseudoStatic$HostKind,
        cls: amendedClass,
        host: amendedClass,
    };
}

export { AeClass, AeMember, AeMembers, AeStatic, AeStatics, Amendable, PseudoMember, PseudoMember__symbol, PseudoStatic, allAmender, amend, amendMemberOf, amendStaticOf, amenderOf, isAmendatory, newAmendTarget, noopAmender };
//# sourceMappingURL=amend.js.map
